/* @version 1.2.0 — LandingUGCDots: dots + prev/next arrow navigation + aria-live position announcer */
class LandingUGCDots extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.mq = window.matchMedia('(max-width: 749px)');
    this.setup();
    this.mq.addEventListener('change', () => this.setup());
  }

  ensureLiveRegion() {
    if (this._live) return;
    this._live = document.createElement('span');
    this._live.className = 'visually-hidden';
    this._live.setAttribute('aria-live', 'polite');
    this._live.setAttribute('aria-atomic', 'true');
    this._live.setAttribute('role', 'status');
    this.appendChild(this._live);
  }

  announce(idx, total) {
    if (!this._live) return;
    this._live.textContent = 'Slide ' + (idx + 1) + ' of ' + total;
  }

  setup() {
    this.teardown();
    if (!this.mq.matches) return;
    const section = this.closest('.landing-ugc');
    this.grid = section && section.querySelector('.landing-ugc__grid.lugc-mobile-slider');
    if (!this.grid) return;
    this.cards = this.grid.querySelectorAll('.landing-ugc__card');
    if (!this.cards.length) return;
    this.dots = this.querySelectorAll('.landing-ugc__dot');
    this.currentIndex = 0;
    this.handlers = [];
    this.ensureLiveRegion();

    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      root: this.grid,
      threshold: 0.6,
    });
    this.cards.forEach((c) => this.observer.observe(c));

    this.dots.forEach((dot, i) => {
      const fn = () => this.goTo(i);
      dot.addEventListener('click', fn);
      this.handlers.push({ el: dot, fn });
    });

    this.prevBtn = section.querySelector('.landing-ugc__arrow--prev');
    this.nextBtn = section.querySelector('.landing-ugc__arrow--next');
    if (this.prevBtn) {
      const fn = () => this.goTo(this.currentIndex - 1);
      this.prevBtn.addEventListener('click', fn);
      this.handlers.push({ el: this.prevBtn, fn });
    }
    if (this.nextBtn) {
      const fn = () => this.goTo(this.currentIndex + 1);
      this.nextBtn.addEventListener('click', fn);
      this.handlers.push({ el: this.nextBtn, fn });
    }

    this.updateArrows();
  }

  goTo(i) {
    const clamped = Math.max(0, Math.min(i, this.cards.length - 1));
    this.cards[clamped].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  onIntersect(entries) {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const idx = Array.prototype.indexOf.call(this.cards, e.target);
      if (idx < 0) return;
      this.currentIndex = idx;
      this.dots.forEach((d, i) => {
        const active = i === idx;
        d.classList.toggle('is-active', active);
        if (active) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      this.announce(idx, this.cards.length);
      this.updateArrows();
    });
  }

  updateArrows() {
    if (this.prevBtn) {
      const atStart = this.currentIndex <= 0;
      this.prevBtn.disabled = atStart;
      this.prevBtn.setAttribute('aria-disabled', String(atStart));
    }
    if (this.nextBtn) {
      const atEnd = this.currentIndex >= this.cards.length - 1;
      this.nextBtn.disabled = atEnd;
      this.nextBtn.setAttribute('aria-disabled', String(atEnd));
    }
  }

  teardown() {
    if (this.observer) { this.observer.disconnect(); this.observer = null; }
    if (this.handlers) {
      this.handlers.forEach(({ el, fn }) => el.removeEventListener('click', fn));
      this.handlers = [];
    }
    this.prevBtn = null;
    this.nextBtn = null;
  }

  disconnectedCallback() { this.teardown(); }
}

if (!customElements.get('landing-ugc-dots')) {
  customElements.define('landing-ugc-dots', LandingUGCDots);
}
